import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  LayoutDashboard, Kanban, Briefcase, Users, Building2, Activity,
  UserCheck, Calendar, Sparkles, MapPin, Gift, MessageSquarePlus,
  Plus, Search, MoreHorizontal, Eye, Edit, Trash2, Check, X, Upload, Compass,
  ChevronRight, ChevronLeft, DollarSign, TrendingUp, ArrowUpRight, Clock,
  Phone, Mail, Video, FileText, ClipboardList, Bell, Target,
  Globe, Star, ExternalLink, Filter, Download, LogOut, Home,
  CheckCircle, AlertCircle, Loader2, Settings, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

// Navigation structure
const navSections = [
  {
    title: 'OVERVIEW',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ]
  },
  {
    title: 'SALES CRM',
    items: [
      { id: 'pipeline', label: 'Pipeline', icon: Kanban },
      { id: 'deals', label: 'Deals', icon: Briefcase },
      { id: 'contacts', label: 'Contacts', icon: Users },
      { id: 'organizations', label: 'Organizations', icon: Building2 },
      { id: 'activities', label: 'Activities', icon: Activity },
    ]
  },
  {
    title: 'OPERATIONS',
    items: [
      { id: 'partners', label: 'Partners', icon: UserCheck },
      { id: 'events', label: 'Events', icon: Calendar },
      { id: 'experiences', label: 'Experiences', icon: Sparkles },
      { id: 'venues', label: 'Venues', icon: MapPin },
      { id: 'date-nights', label: 'Date Nights', icon: Gift },
      { id: 'curated-guides', label: 'Curated Guides', icon: Compass },
    ]
  },
  {
    title: 'USERS & FEEDBACK',
    items: [
      { id: 'users', label: 'Users', icon: Users },
      { id: 'gold-members', label: 'Gold Members', icon: Star },
      { id: 'feedback', label: 'Feedback', icon: MessageSquarePlus },
    ]
  },
  {
    title: 'SYSTEM',
    items: [
      { id: 'reminders', label: 'Reminders', icon: Bell },
    ]
  }
];

// CRM Configuration
const PIPELINE_STAGES = [
  { id: 'lead', name: 'Lead', color: '#6366f1' },
  { id: 'contacted', name: 'Contacted', color: '#8b5cf6' },
  { id: 'demo_scheduled', name: 'Demo Scheduled', color: '#a855f7' },
  { id: 'proposal_sent', name: 'Proposal Sent', color: '#ec4899' },
  { id: 'negotiation', name: 'Negotiation', color: '#f97316' },
  { id: 'won', name: 'Won', color: '#22c55e' },
  { id: 'lost', name: 'Lost', color: '#ef4444' },
];

const LEAD_SOURCES = {
  website: 'Website', referral: 'Referral', cold_outreach: 'Cold Outreach',
  event: 'Event', social_media: 'Social Media', advertising: 'Advertising',
  partner: 'Partner', other: 'Other'
};

const ACTIVITY_TYPES = {
  call: { icon: Phone, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Call' },
  email: { icon: Mail, color: 'text-green-500', bg: 'bg-green-50', label: 'Email' },
  meeting: { icon: Video, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Meeting' },
  note: { icon: FileText, color: 'text-gray-500', bg: 'bg-gray-50', label: 'Note' },
  task: { icon: ClipboardList, color: 'text-orange-500', bg: 'bg-orange-50', label: 'Task' },
  demo: { icon: Target, color: 'text-pink-500', bg: 'bg-pink-50', label: 'Demo' },
  follow_up: { icon: Bell, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Follow-up' },
};

const INDUSTRIES = ['Restaurant', 'Bar', 'Brewery', 'Winery', 'Coffee Shop', 'Event Venue', 'Hotel', 'Gallery', 'Theater', 'Other'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Data states
  const [stats, setStats] = useState({});
  const [crmStats, setCrmStats] = useState({});
  const [pipelineStats, setPipelineStats] = useState([]);
  const [pipelineData, setPipelineData] = useState({});
  const [reminderStatus, setReminderStatus] = useState(null);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  
  // Lists
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [dateNightPackages, setDateNightPackages] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({});
  
  // CRM Lists
  const [crmOrganizations, setCrmOrganizations] = useState([]);
  const [crmContacts, setCrmContacts] = useState([]);
  const [crmDeals, setCrmDeals] = useState([]);
  const [crmActivities, setCrmActivities] = useState([]);

  // Dialog states
  const [showOrgDialog, setShowOrgDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showDealDialog, setShowDealDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showExperienceDialog, setShowExperienceDialog] = useState(false);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showPartnerDialog, setShowPartnerDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showVenueEditDialog, setShowVenueEditDialog] = useState(false);
  const [adminPartnerStep, setAdminPartnerStep] = useState(1);
  const [adminGenDesc, setAdminGenDesc] = useState(false);
  const [adminGenExpect, setAdminGenExpect] = useState(false);
  const [adminPlaceQuery, setAdminPlaceQuery] = useState('');
  const [adminPlaceResults, setAdminPlaceResults] = useState([]);
  const [adminPlaceSearching, setAdminPlaceSearching] = useState(false);
  
  const [editingItem, setEditingItem] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [guideTemplates, setGuideTemplates] = useState([]);
  const [showGuideDialog, setShowGuideDialog] = useState(false);
  const [guideForm, setGuideForm] = useState({ title: '', description: '', icon: 'star', prompts: [] });

  // Form states
  const [orgForm, setOrgForm] = useState({ name: '', industry: '', website: '', phone: '', email: '', address: '', city: '', state: '', zip_code: '', description: '' });
  const [contactForm, setContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', job_title: '', organization_id: '', lead_source: '', notes: '' });
  const [dealForm, setDealForm] = useState({ title: '', value: 0, stage: 'lead', probability: 10, expected_close_date: '', organization_id: '', contact_id: '', description: '' });
  const [activityForm, setActivityForm] = useState({ type: 'call', subject: '', description: '', deal_id: '', contact_id: '', organization_id: '', due_date: '' });
  const [eventForm, setEventForm] = useState({ title: '', description: '', venue_id: '', date: '', time: '', price: 0, category: 'music', capacity: 100, commission_rate: '' });
  const [experienceForm, setExperienceForm] = useState({ title: '', description: '', venue_id: '', price: 0, duration: '', category: '', valid_until: '', commission_rate: '' });
  const [partnerForm, setPartnerForm] = useState({ full_name: '', email: '', business_name: '', venue_type: 'Bar & Restaurant', price_level: '$$', phone: '', website: '', address: '', city: 'Denver', state: 'CO', zip_code: '', neighborhood: 'rino', capacity: 50, business_description: '', what_to_expect: '', amenities: [], photos: [], menu_url: '', business_hours: ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => ({day:d,open_time:'11:00',close_time:'23:00',is_closed:false})), accepts_reservations: false, reservation_settings: { card_required: true, no_show_fee: 25, cancellation_hours: 24, advance_booking_days: 30 }, experiences: [] });
  const [adminGenExp, setAdminGenExp] = useState(false);
  const [createdPartnerCreds, setCreatedPartnerCreds] = useState(null);
  const [userForm, setUserForm] = useState({ full_name: '', email: '', password: '', role: 'consumer' });
  const [feedbackEdits, setFeedbackEdits] = useState({ status: '', priority: '', admin_notes: '' });
  const [feedbackResponse, setFeedbackResponse] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/auth');
      return;
    }
    fetchAllData();
    // Load Google Maps for Places search
    if (!document.getElementById('google-maps-admin')) {
      const s = document.createElement('script');
      s.id = 'google-maps-admin';
      s.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g&libraries=places';
      s.async = true;
      document.head.appendChild(s);
    }
  }, [isAuthenticated, authLoading, user]);

  const fetchAllData = async () => {
    try {
      const [
        dashboardRes, appsRes, usersRes, venuesRes, eventsRes, experiencesRes,
        packagesRes, feedbackRes, crmDashRes, orgsRes, contactsRes, dealsRes,
        activitiesRes, pipelineRes, reminderRes, guidesRes
      ] = await Promise.all([
        api.get('/admin/dashboard').catch(() => ({ data: { stats: {} } })),
        api.get('/admin/applications').catch(() => ({ data: { applications: [] } })),
        api.get('/admin/users').catch(() => ({ data: { users: [] } })),
        api.get('/admin/venues').catch(() => ({ data: { venues: [] } })),
        api.get('/events').catch(() => ({ data: [] })),
        api.get('/vouchers').catch(() => ({ data: [] })),
        api.get('/date-night-packages?include_inactive=true').catch(() => ({ data: { packages: [] } })),
        api.get('/feedback/admin/all').catch(() => ({ data: { feedback: [], stats: {} } })),
        api.get('/crm/dashboard').catch(() => ({ data: { stats: {}, pipeline_stats: [] } })),
        api.get('/crm/organizations').catch(() => ({ data: { organizations: [] } })),
        api.get('/crm/contacts').catch(() => ({ data: { contacts: [] } })),
        api.get('/crm/deals').catch(() => ({ data: { deals: [] } })),
        api.get('/crm/activities').catch(() => ({ data: { activities: [] } })),
        api.get('/crm/deals/pipeline').catch(() => ({ data: {} })),
        api.get('/admin/reminders/status').catch(() => ({ data: { reservations: [] } })),
        api.get('/guides/templates').catch(() => ({ data: { templates: [] } })),
      ]);

      setStats(dashboardRes.data.stats || {});
      setApplications(appsRes.data.applications || []);
      setUsers(usersRes.data.users || []);
      setVenues(venuesRes.data.venues || []);
      setEvents(eventsRes.data || []);
      setExperiences(experiencesRes.data || []);
      setDateNightPackages(packagesRes.data.packages || []);
      setFeedbackList(feedbackRes.data.feedback || []);
      setFeedbackStats(feedbackRes.data.stats || {});
      setCrmStats(crmDashRes.data.stats || {});
      setPipelineStats(crmDashRes.data.pipeline_stats || []);
      setCrmOrganizations(orgsRes.data.organizations || []);
      setCrmContacts(contactsRes.data.contacts || []);
      setCrmDeals(dealsRes.data.deals || []);
      setCrmActivities(activitiesRes.data.activities || []);
      setPipelineData(pipelineRes.data || {});
      setReminderStatus(reminderRes.data || { reservations: [] });
      setGuideTemplates(guidesRes.data.templates || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Reminder trigger handler
  const handleTriggerReminders = async () => {
    setTriggeringReminders(true);
    try {
      const response = await api.post('/admin/reminders/trigger');
      const results = response.data.results;
      const total24h = results['24h_reminders'];
      const total2h = results['2h_reminders'];
      
      toast.success(`Reminders sent: ${total24h.customer_emails_sent + total2h.customer_emails_sent} emails, ${total24h.customer_sms_sent + total2h.customer_sms_sent} SMS`);
      
      // Refresh reminder status
      const statusRes = await api.get('/admin/reminders/status');
      setReminderStatus(statusRes.data || { reservations: [] });
    } catch (error) {
      toast.error('Failed to trigger reminders');
    } finally {
      setTriggeringReminders(false);
    }
  };

  // CRM Handlers
  const handleSaveOrg = async () => {
    if (!orgForm.name) { toast.error('Organization name required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/crm/organizations/${editingItem.id}`, orgForm);
        toast.success('Organization updated');
      } else {
        await api.post('/crm/organizations', orgForm);
        toast.success('Organization created');
      }
      setShowOrgDialog(false);
      setOrgForm({ name: '', industry: '', website: '', phone: '', email: '', address: '', city: '', state: '', zip_code: '', description: '' });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save organization');
    } finally { setSaving(false); }
  };

  const handleSaveContact = async () => {
    if (!contactForm.first_name || !contactForm.last_name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/crm/contacts/${editingItem.id}`, contactForm);
        toast.success('Contact updated');
      } else {
        await api.post('/crm/contacts', contactForm);
        toast.success('Contact created');
      }
      setShowContactDialog(false);
      setContactForm({ first_name: '', last_name: '', email: '', phone: '', job_title: '', organization_id: '', lead_source: '', notes: '' });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save contact');
    } finally { setSaving(false); }
  };

  const handleSaveDeal = async () => {
    if (!dealForm.title) { toast.error('Deal title required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/crm/deals/${editingItem.id}`, dealForm);
        toast.success('Deal updated');
      } else {
        await api.post('/crm/deals', dealForm);
        toast.success('Deal created');
      }
      setShowDealDialog(false);
      setDealForm({ title: '', value: 0, stage: 'lead', probability: 10, expected_close_date: '', organization_id: '', contact_id: '', description: '' });
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save deal');
    } finally { setSaving(false); }
  };

  const handleSaveActivity = async () => {
    if (!activityForm.subject) { toast.error('Subject required'); return; }
    if (!activityForm.deal_id && !activityForm.contact_id && !activityForm.organization_id) {
      toast.error('Link to a deal, contact, or organization'); return;
    }
    setSaving(true);
    try {
      await api.post('/crm/activities', activityForm);
      toast.success('Activity logged');
      setShowActivityDialog(false);
      setActivityForm({ type: 'call', subject: '', description: '', deal_id: '', contact_id: '', organization_id: '', due_date: '' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to log activity');
    } finally { setSaving(false); }
  };

  const handleDealStageChange = async (dealId, newStage) => {
    try {
      await api.put(`/crm/deals/${dealId}/stage?stage=${newStage}`);
      toast.success('Stage updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  // Event Handlers
  const handleSaveEvent = async () => {
    if (!eventForm.title || !eventForm.venue_id) { toast.error('Title and venue required'); return; }
    setSaving(true);
    try {
      const venue = venues.find(v => v.id === eventForm.venue_id);
      const payload = {
        title: eventForm.title,
        description: eventForm.description,
        date: eventForm.date,
        time: eventForm.time,
        price: eventForm.price,
        category: eventForm.category,
        capacity: eventForm.capacity,
        venue_name: venue?.name || '',
        images: [],
        gold_exclusive: false,
        is_published: true,
        commission_rate: eventForm.commission_rate ? parseFloat(eventForm.commission_rate) : null
      };
      await api.post(`/admin/venues/${eventForm.venue_id}/create-event`, payload);
      toast.success('Event created');
      setShowEventDialog(false);
      setEventForm({ title: '', description: '', venue_id: '', date: '', time: '', price: 0, category: 'music', capacity: 100, commission_rate: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally { setSaving(false); }
  };

  const toggleEventPublished = async (eventId, currentStatus) => {
    try {
      await api.put(`/events/${eventId}`, { is_published: !currentStatus });
      toast.success(currentStatus ? 'Event unpublished' : 'Event published');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Experience Handlers
  const handleSaveExperience = async () => {
    if (!experienceForm.title || !experienceForm.venue_id) { toast.error('Title and venue required'); return; }
    setSaving(true);
    try {
      const venue = venues.find(v => v.id === experienceForm.venue_id);
      const payload = {
        title: experienceForm.title,
        description: experienceForm.description,
        price: experienceForm.price,
        valid_until: experienceForm.valid_until || new Date(Date.now() + 365*24*60*60*1000).toISOString().split('T')[0],
        venue_name: venue?.name || '',
        images: [],
        whats_included: [],
        terms: [],
        is_date_night: false,
        is_published: true,
        commission_rate: experienceForm.commission_rate ? parseFloat(experienceForm.commission_rate) : null
      };
      await api.post(`/admin/venues/${experienceForm.venue_id}/create-experience`, payload);
      toast.success('Experience created');
      setShowExperienceDialog(false);
      setExperienceForm({ title: '', description: '', venue_id: '', price: 0, duration: '', category: '', valid_until: '', commission_rate: '' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create experience');
    } finally { setSaving(false); }
  };

  // Feedback Handlers
  const openFeedbackDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setFeedbackResponse(feedback.admin_response || '');
    setFeedbackEdits({ status: feedback.status || 'new', priority: feedback.priority || 'medium', admin_notes: feedback.admin_notes || '' });
    setShowFeedbackDialog(true);
  };

  const handleSaveFeedback = async () => {
    if (!selectedFeedback) return;
    setSaving(true);
    try {
      await api.put(`/feedback/admin/${selectedFeedback.id}`, {
        status: feedbackEdits.status,
        priority: feedbackEdits.priority,
        admin_notes: feedbackEdits.admin_notes,
        admin_response: feedbackResponse || null
      });
      toast.success('Feedback updated');
      setShowFeedbackDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update feedback');
    } finally { setSaving(false); }
  };

  // Partner Handlers
  const handleApplicationAction = async (appId, action) => {
    try {
      await api.post(`/admin/applications/${appId}/${action}`);
      toast.success(`Application ${action}ed`);
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to ${action} application`);
    }
  };

  const handleDeleteApplication = async (appId) => {
    try {
      await api.delete(`/admin/applications/${appId}`);
      toast.success('Application deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete application');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role?new_role=${newRole}`);
      toast.success(`Role updated to ${newRole}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleCreatePartner = async () => {
    if (!partnerForm.full_name || !partnerForm.email || !partnerForm.business_name) {
      toast.error('Name, email, and business name are required'); return;
    }
    setSaving(true);
    try {
      const res = await api.post('/admin/partners/create', partnerForm);
      setCreatedPartnerCreds({ email: res.data.email, password: res.data.temporary_password });
      setAdminPartnerStep(10); // Show credentials step
      toast.success('Partner created and auto-approved!');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    } finally { setSaving(false); }
  };

  const adminGenerateExperience = async (expName) => {
    if (!expName) { toast.error('Enter an experience name'); return; }
    setAdminGenExp(true);
    try {
      const res = await api.post('/onboarding/generate-experience', {
        experience_name: expName,
        venue_name: partnerForm.business_name,
        venue_type: partnerForm.venue_type,
      });
      return res.data;
    } catch { toast.error('Failed to generate'); return null; }
    finally { setAdminGenExp(false); }
  };

  const adminGenerateDesc = async () => {
    if (!partnerForm.business_name) { toast.error('Enter a business name first'); return; }
    setAdminGenDesc(true);
    try {
      const res = await api.post('/onboarding/generate-description', {
        business_name: partnerForm.business_name, venue_type: partnerForm.venue_type,
        neighborhood: partnerForm.neighborhood || 'Denver', website: partnerForm.website,
        existing_description: partnerForm.business_description,
      });
      setPartnerForm(prev => ({...prev, business_description: res.data.description}));
      toast.success('Description generated!');
    } catch { toast.error('Failed to generate'); }
    finally { setAdminGenDesc(false); }
  };

  const adminGenerateExpect = async () => {
    if (!partnerForm.business_name) { toast.error('Enter a business name first'); return; }
    setAdminGenExpect(true);
    try {
      const res = await api.post('/onboarding/generate-description', {
        business_name: partnerForm.business_name, venue_type: partnerForm.venue_type,
        neighborhood: partnerForm.neighborhood || 'Denver',
        existing_description: partnerForm.business_description,
        what_to_expect: 'GENERATE_WHAT_TO_EXPECT',
      });
      setPartnerForm(prev => ({...prev, what_to_expect: res.data.description}));
      toast.success('"What to Expect" generated!');
    } catch { toast.error('Failed to generate'); }
    finally { setAdminGenExpect(false); }
  };

  const adminSearchPlace = async (query) => {
    setAdminPlaceQuery(query);
    if (query.length < 3) { setAdminPlaceResults([]); return; }
    
    // Wait for Google Maps to load
    const waitForGoogle = () => new Promise((resolve) => {
      if (window.google?.maps?.places) { resolve(); return; }
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve(); }
      }, 200);
      setTimeout(() => { clearInterval(check); resolve(); }, 5000);
    });
    
    await waitForGoogle();
    if (!window.google?.maps?.places) { toast.error('Google Places not loaded. Try again.'); return; }
    
    setAdminPlaceSearching(true);
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: query, types: ['establishment'], componentRestrictions: { country: 'us' } },
      (predictions, status) => {
        setAdminPlaceSearching(false);
        setAdminPlaceResults(status === 'OK' && predictions ? predictions.slice(0, 5) : []);
      }
    );
  };

  const adminSelectPlace = async (placeId) => {
    if (!window.google?.maps?.places) return;
    const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
    const place = await new Promise((resolve) => {
      svc.getDetails({ placeId, fields: ['name','formatted_address','address_components','formatted_phone_number','website','types','photos','opening_hours'] },
        (r, s) => resolve(s === 'OK' ? r : null));
    });
    if (!place) return;
    const get = (type) => place.address_components?.find(c => c.types.includes(type))?.long_name || '';
    const getShort = (type) => place.address_components?.find(c => c.types.includes(type))?.short_name || '';
    const streetNum = get('street_number'); const route = get('route');
    const typeMap = {'restaurant':'Bar & Restaurant','bar':'Bar & Restaurant','night_club':'Cocktail Lounge','cafe':'Coffee Shop','brewery':'Brewery','art_gallery':'Art Gallery','event_venue':'Event Space'};
    let venueType = ''; for (const gt of (place.types||[])) { if (typeMap[gt]) { venueType = typeMap[gt]; break; } }
    const nhMap = {'river north':'rino','rino':'rino','cherry creek':'cherry-creek','lodo':'lodo','lower downtown':'lodo','south broadway':'sobo','highland':'highland-lohi','lohi':'highland-lohi','capitol hill':'capitol-hill','tennyson':'tennyson','berkeley':'tennyson'};
    const nh = get('neighborhood') || get('sublocality_level_1') || '';
    let matchedNh = ''; for (const [k,v] of Object.entries(nhMap)) { if (nh.toLowerCase().includes(k)) { matchedNh = v; break; } }
    const GKEY = 'AIzaSyCHJPkfGCpXIT0225976P3tGS2rcL2xI_g';
    const rawPhotoRefs = place.photos ? place.photos.slice(0, 6).map(p => {
      // Build a server-downloadable URL using Places Photo API
      const ref = p.getUrl ? p.getUrl({ maxWidth: 1200 }) : '';
      const photoRef = p.photo_reference || '';
      const apiUrl = photoRef ? `https://maps.googleapis.com/maps/api/place/photo?maxwidth=1200&photo_reference=${photoRef}&key=${GKEY}` : ref;
      return { url: ref, apiUrl };
    }) : [];
    let businessHours = null;
    if (place.opening_hours?.weekday_text) {
      const dayMap = {'monday':0,'tuesday':1,'wednesday':2,'thursday':3,'friday':4,'saturday':5,'sunday':6};
      businessHours = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => ({day:d,open_time:'11:00',close_time:'23:00',is_closed:false}));
      for (const line of place.opening_hours.weekday_text) {
        const m = line.match(/^(\w+):\s*(.+)$/); if (!m) continue;
        const idx = dayMap[m[1].toLowerCase()]; if (idx === undefined) continue;
        if (m[2].trim().toLowerCase() === 'closed') { businessHours[idx].is_closed = true; }
        else { const parts = m[2].split('\u2013').map(s => s.trim()); if (parts.length === 2) { const to24 = (t) => { const mx = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i); if(!mx)return'12:00'; let h=parseInt(mx[1]); if(mx[3].toUpperCase()==='PM'&&h!==12)h+=12; if(mx[3].toUpperCase()==='AM'&&h===12)h=0; return `${h.toString().padStart(2,'0')}:${mx[2]}`; }; businessHours[idx].open_time = to24(parts[0]); businessHours[idx].close_time = to24(parts[1]); } }
      }
    }
    const featureMap = {'restaurant':['Food Menu'],'bar':['Full Bar','Late Night'],'night_club':['Dance Floor','DJ','Late Night'],'cafe':['Free WiFi'],'brewery':['Craft Beer']};
    let amenities = []; for (const gt of (place.types||[])) { if (featureMap[gt]) amenities.push(...featureMap[gt]); } amenities = [...new Set(amenities)];
    let persistedPhotos = [];
    if (rawPhotoRefs.length > 0) {
      toast.info('Downloading ' + rawPhotoRefs.length + ' photos...');
      for (const photo of rawPhotoRefs) {
        // Try server-side download with Places Photo API URL first
        if (photo.apiUrl && photo.apiUrl.includes('photo_reference')) {
          try {
            const res = await api.post('/upload/from-url', { url: photo.apiUrl });
            if (res.data?.url) { persistedPhotos.push(res.data.url); continue; }
          } catch {}
        }
        // Try server-side with JS URL
        try {
          const res = await api.post('/upload/from-url', { url: photo.url });
          if (res.data?.url) { persistedPhotos.push(res.data.url); continue; }
        } catch {}
        // Fallback: client-side fetch
        try {
          const resp = await fetch(photo.url, { mode: 'cors' });
          if (resp.ok) {
            const blob = await resp.blob();
            const fd = new FormData();
            fd.append('file', blob, 'photo.jpg');
            const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.url) persistedPhotos.push(res.data.url);
          }
        } catch {}
      }
    }
    setPartnerForm(prev => ({...prev, business_name: place.name||prev.business_name, address: (streetNum+' '+route).trim()||prev.address, city: get('locality')||get('sublocality')||prev.city, state: getShort('administrative_area_level_1')||prev.state, zip_code: get('postal_code')||prev.zip_code, phone: place.formatted_phone_number||prev.phone, website: place.website||prev.website, venue_type: venueType||prev.venue_type, neighborhood: matchedNh||prev.neighborhood, ...(businessHours?{business_hours:businessHours}:{}), ...(amenities.length>0?{amenities:[...new Set([...(prev.amenities||[]),...amenities])]}:{}), ...(persistedPhotos.length>0?{photos:persistedPhotos}:{})}));
    setAdminPlaceQuery(''); setAdminPlaceResults([]);
    toast.success('Imported ' + place.name + (persistedPhotos.length > 0 ? ' \u2014 ' + persistedPhotos.length + ' photos' : ' (use Upload button to add photos)'));
  };


  const handleSaveUser = async () => {
    if (!userForm.full_name || !userForm.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/admin/users/${editingItem.id}`, { full_name: userForm.full_name, email: userForm.email, role: userForm.role });
        toast.success('User updated');
      } else {
        if (!userForm.password || userForm.password.length < 6) { toast.error('Password must be at least 6 characters'); setSaving(false); return; }
        await api.post('/auth/register', { full_name: userForm.full_name, email: userForm.email, password: userForm.password, role: userForm.role });
        toast.success('User created');
      }
      setShowUserDialog(false);
      setEditingItem(null);
      setUserForm({ full_name: '', email: '', password: '', role: 'consumer' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally { setSaving(false); }
  };

  const openEditUser = (u) => {
    setEditingItem(u);
    setUserForm({ full_name: u.full_name || '', email: u.email || '', password: '', role: u.role || 'consumer' });
    setShowUserDialog(true);
  };

  const handleDeleteVenue = async (venueId) => {
    try {
      await api.delete(`/admin/venues/${venueId}`);
      toast.success('Venue deleted');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete venue');
    }
  };

  const handleSaveVenueEdit = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      const updates = {};
      ['name', 'description', 'address', 'phone', 'email', 'website', 'category', 'price_level', 'capacity', 'neighborhood', 'status'].forEach(f => {
        if (editingItem[f] !== undefined) updates[f] = editingItem[f];
      });
      await api.put(`/admin/venues/${editingItem.id}`, updates);
      toast.success('Venue updated');
      setShowVenueEditDialog(false);
      setEditingItem(null);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update venue');
    } finally { setSaving(false); }
  };

  // Edit handlers
  const openEditOrg = (org) => {
    setEditingItem(org);
    setOrgForm({ name: org.name || '', industry: org.industry || '', website: org.website || '', phone: org.phone || '', email: org.email || '', address: org.address || '', city: org.city || '', state: org.state || '', zip_code: org.zip_code || '', description: org.description || '' });
    setShowOrgDialog(true);
  };

  const openEditContact = (contact) => {
    setEditingItem(contact);
    setContactForm({ first_name: contact.first_name || '', last_name: contact.last_name || '', email: contact.email || '', phone: contact.phone || '', job_title: contact.job_title || '', organization_id: contact.organization_id || '', lead_source: contact.lead_source || '', notes: contact.notes || '' });
    setShowContactDialog(true);
  };

  const openEditDeal = (deal) => {
    setEditingItem(deal);
    setDealForm({ title: deal.title || '', value: deal.value || 0, stage: deal.stage || 'lead', probability: deal.probability || 10, expected_close_date: deal.expected_close_date || '', organization_id: deal.organization_id || '', contact_id: deal.contact_id || '', description: deal.description || '' });
    setShowDealDialog(true);
  };

  // Delete handlers
  const handleDelete = async (type, id) => {
    try {
      await api.delete(`/crm/${type}s/${id}`);
      toast.success(`${type} deleted`);
      fetchAllData();
    } catch (error) {
      toast.error(`Failed to delete ${type}`);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Filter functions
  const filteredEvents = events.filter(e => 
    !searchTerm || e.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredExperiences = experiences.filter(e => 
    !searchTerm || e.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFeedback = feedbackList.filter(f =>
    !searchTerm || f.title?.toLowerCase().includes(searchTerm.toLowerCase()) || f.user_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white flex flex-col fixed h-full z-50">
        {/* Logo */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">EVENT★GOERS</h1>
              <p className="text-xs text-slate-400">Admin Console</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4">
          {navSections.map((section) => (
            <div key={section.title} className="mb-4">
              <p className="px-4 mb-2 text-xs font-semibold text-slate-500 tracking-wider">
                {section.title}
              </p>
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                const badgeCount = item.id === 'feedback' ? feedbackStats?.new : 
                                   item.id === 'partners' ? applications.filter(a => a.status === 'pending').length : 0;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`${item.id}-tab`}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-indigo-600 text-white'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {badgeCount > 0 && (
                      <Badge className="bg-red-500 text-white text-xs">{badgeCount}</Badge>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Quick Actions */}
        <div className="p-3 border-t border-slate-800 space-y-2">
          <Button size="sm" className="w-full bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditingItem(null); setDealForm({ title: '', value: 0, stage: 'lead', probability: 10, expected_close_date: '', organization_id: '', contact_id: '', description: '' }); setShowDealDialog(true); }}>
            <Plus className="w-4 h-4 mr-2" /> New Deal
          </Button>
        </div>

        {/* User */}
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-bold">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400">Administrator</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate('/'); }} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b sticky top-0 z-40 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 capitalize">
                {activeTab === 'date-nights' ? 'Date Night Packages' : activeTab.replace('-', ' ')}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  className="pl-9 w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" /> View Site
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-indigo-100 text-sm">Pipeline Value</p>
                        <p className="text-3xl font-bold">${crmStats.total_pipeline_value?.toLocaleString() || 0}</p>
                      </div>
                      <DollarSign className="w-10 h-10 text-indigo-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Open Deals</p>
                        <p className="text-3xl font-bold">{crmStats.total_deals || 0}</p>
                      </div>
                      <Briefcase className="w-10 h-10 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Total Partners</p>
                        <p className="text-3xl font-bold">{stats.total_partners || 0}</p>
                      </div>
                      <UserCheck className="w-10 h-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm">Active Events</p>
                        <p className="text-3xl font-bold">{events.filter(e => e.is_published).length}</p>
                      </div>
                      <Calendar className="w-10 h-10 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Overview */}
              <Card className="border-2 border-green-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Financial Overview
                  </CardTitle>
                  <CardDescription>Platform revenue and earnings summary</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-green-50 rounded-xl">
                      <p className="text-sm text-green-700 font-medium">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-800">${(stats.total_revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-green-600 mt-1">All-time gross sales</p>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-xl">
                      <p className="text-sm text-indigo-700 font-medium">Platform Commission</p>
                      <p className="text-3xl font-bold text-indigo-800">${(stats.platform_commission || 0).toLocaleString()}</p>
                      <p className="text-xs text-indigo-600 mt-1">EventGoers earnings</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-xl">
                      <p className="text-sm text-purple-700 font-medium">Ticket Sales</p>
                      <p className="text-3xl font-bold text-purple-800">${(stats.ticket_revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-purple-600 mt-1">Event ticket revenue</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-xl">
                      <p className="text-sm text-amber-700 font-medium">Gold Memberships</p>
                      <p className="text-3xl font-bold text-amber-800">${(stats.gold_membership_revenue || 0).toLocaleString()}</p>
                      <p className="text-xs text-amber-600 mt-1">{stats.gold_members || 0} active members</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="p-4 bg-pink-50 rounded-xl">
                      <p className="text-sm text-pink-700 font-medium">Experience Sales</p>
                      <p className="text-2xl font-bold text-pink-800">${(stats.experience_revenue || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-xl">
                      <p className="text-sm text-blue-700 font-medium">Total Bookings</p>
                      <p className="text-2xl font-bold text-blue-800">{stats.total_bookings || 0}</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-sm text-slate-700 font-medium">Total Reservations</p>
                      <p className="text-2xl font-bold text-slate-800">{stats.total_reservations || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pipeline Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Sales Pipeline</CardTitle>
                  <CardDescription>Deal progress by stage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {pipelineStats.filter(s => s.id !== 'won' && s.id !== 'lost').map(stage => (
                      <div key={stage.id} className="flex-1 p-4 rounded-lg border-2 border-dashed text-center" style={{ borderColor: stage.color + '40' }}>
                        <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: stage.color }} />
                        <p className="text-xs text-gray-500">{stage.name}</p>
                        <p className="text-2xl font-bold">{stage.count || 0}</p>
                        <p className="text-xs text-gray-400">${(stage.value || 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Pending Applications</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold text-amber-500">{applications.filter(a => a.status === 'pending').length}</p>
                    <Button variant="link" className="p-0 h-auto mt-2" onClick={() => setActiveTab('partners')}>
                      Review now <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">New Feedback</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold text-blue-500">{feedbackStats?.new || 0}</p>
                    <Button variant="link" className="p-0 h-auto mt-2" onClick={() => setActiveTab('feedback')}>
                      View feedback <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Total Users</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-4xl font-bold">{stats.total_users || 0}</p>
                    <p className="text-sm text-gray-500 mt-2">{stats.gold_members || 0} Gold Members</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* PIPELINE TAB (Kanban) */}
          {activeTab === 'pipeline' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setDealForm({ title: '', value: 0, stage: 'lead', probability: 10, expected_close_date: '', organization_id: '', contact_id: '', description: '' }); setShowDealDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> New Deal
                </Button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4">
                {PIPELINE_STAGES.filter(s => s.id !== 'won' && s.id !== 'lost').map(stage => {
                  const stageDeals = crmDeals.filter(d => d.stage === stage.id);
                  return (
                    <div key={stage.id} className="flex-shrink-0 w-72 bg-gray-100 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stage.color }} />
                          <span className="font-semibold text-sm">{stage.name}</span>
                          <Badge variant="secondary">{stageDeals.length}</Badge>
                        </div>
                      </div>
                      <div className="space-y-2 min-h-[200px]">
                        {stageDeals.map(deal => {
                          const stageIdx = PIPELINE_STAGES.findIndex(s => s.id === stage.id);
                          const prevStage = stageIdx > 0 ? PIPELINE_STAGES[stageIdx - 1] : null;
                          const nextStage = stageIdx < PIPELINE_STAGES.length - 1 ? PIPELINE_STAGES[stageIdx + 1] : null;
                          return (
                          <div key={deal.id} className="bg-white rounded-lg p-3 shadow-sm border hover:shadow-md">
                            <div className="cursor-pointer" onClick={() => openEditDeal(deal)}>
                              <h4 className="font-medium text-sm">{deal.title}</h4>
                              <p className="text-lg font-bold">${deal.value?.toLocaleString() || 0}</p>
                              {deal.organization_name && <p className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" />{deal.organization_name}</p>}
                            </div>
                            <div className="flex justify-between mt-2 pt-2 border-t">
                              {prevStage ? (
                                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={(e) => { e.stopPropagation(); handleDealStageChange(deal.id, prevStage.id); }}>
                                  <ChevronLeft className="w-3 h-3 mr-1" />{prevStage.name}
                                </Button>
                              ) : <span />}
                              {nextStage ? (
                                <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={(e) => { e.stopPropagation(); handleDealStageChange(deal.id, nextStage.id); }}>
                                  {nextStage.name}<ChevronRight className="w-3 h-3 ml-1" />
                                </Button>
                              ) : <span />}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {/* Won/Lost columns */}
                <div className="flex-shrink-0 w-72 bg-green-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-sm text-green-700">Won</span>
                    <Badge className="bg-green-100 text-green-700">{crmDeals.filter(d => d.stage === 'won').length}</Badge>
                  </div>
                  <div className="space-y-2 min-h-[100px]">
                    {crmDeals.filter(d => d.stage === 'won').slice(0, 3).map(deal => (
                      <div key={deal.id} className="bg-white rounded-lg p-3 border-green-200 border">
                        <h4 className="font-medium text-sm">{deal.title}</h4>
                        <p className="text-lg font-bold text-green-600">${deal.value?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0 w-72 bg-red-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <X className="w-4 h-4 text-red-600" />
                    <span className="font-semibold text-sm text-red-700">Lost</span>
                    <Badge className="bg-red-100 text-red-700">{crmDeals.filter(d => d.stage === 'lost').length}</Badge>
                  </div>
                  <div className="space-y-2 min-h-[100px]">
                    {crmDeals.filter(d => d.stage === 'lost').slice(0, 3).map(deal => (
                      <div key={deal.id} className="bg-white rounded-lg p-3 border-red-200 border opacity-60">
                        <h4 className="font-medium text-sm">{deal.title}</h4>
                        <p className="text-lg font-bold text-red-600">${deal.value?.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DEALS TAB */}
          {activeTab === 'deals' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setDealForm({ title: '', value: 0, stage: 'lead', probability: 10, expected_close_date: '', organization_id: '', contact_id: '', description: '' }); setShowDealDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> New Deal
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Deal</TableHead>
                        <TableHead>Value</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {crmDeals.map(deal => {
                        const stage = PIPELINE_STAGES.find(s => s.id === deal.stage);
                        return (
                          <TableRow key={deal.id} className="cursor-pointer" onClick={() => openEditDeal(deal)}>
                            <TableCell className="font-medium">{deal.title}</TableCell>
                            <TableCell className="font-bold">${deal.value?.toLocaleString()}</TableCell>
                            <TableCell><Badge style={{ backgroundColor: stage?.color + '20', color: stage?.color }}>{stage?.name}</Badge></TableCell>
                            <TableCell>{deal.organization_name || '-'}</TableCell>
                            <TableCell>{deal.contact_name || '-'}</TableCell>
                            <TableCell className="text-gray-500 text-sm">{format(new Date(deal.created_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell onClick={e => e.stopPropagation()}>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => openEditDeal(deal)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDelete('deal', deal.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CONTACTS TAB */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setContactForm({ first_name: '', last_name: '', email: '', phone: '', job_title: '', organization_id: '', lead_source: '', notes: '' }); setShowContactDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> New Contact
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Lead Source</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {crmContacts.map(contact => (
                        <TableRow key={contact.id} className="cursor-pointer" onClick={() => openEditContact(contact)}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm">
                                {contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                                {contact.job_title && <p className="text-xs text-gray-500">{contact.job_title}</p>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{contact.email || '-'}</TableCell>
                          <TableCell>{contact.phone || '-'}</TableCell>
                          <TableCell>{contact.organization_name || '-'}</TableCell>
                          <TableCell>{contact.lead_source && <Badge variant="outline">{LEAD_SOURCES[contact.lead_source]}</Badge>}</TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditContact(contact)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDelete('contact', contact.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ORGANIZATIONS TAB */}
          {activeTab === 'organizations' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setOrgForm({ name: '', industry: '', website: '', phone: '', email: '', address: '', city: '', state: '', zip_code: '', description: '' }); setShowOrgDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> New Organization
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Organization</TableHead>
                        <TableHead>Industry</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {crmOrganizations.map(org => (
                        <TableRow key={org.id} className="cursor-pointer" onClick={() => openEditOrg(org)}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-slate-600" /></div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline" onClick={e => e.stopPropagation()}>{org.website}</a>}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{org.industry && <Badge variant="outline">{org.industry}</Badge>}</TableCell>
                          <TableCell>{org.city && org.state ? `${org.city}, ${org.state}` : '-'}</TableCell>
                          <TableCell><div className="space-y-1">{org.email && <p className="text-sm flex items-center gap-1"><Mail className="w-3 h-3" />{org.email}</p>}{org.phone && <p className="text-sm flex items-center gap-1"><Phone className="w-3 h-3" />{org.phone}</p>}</div></TableCell>
                          <TableCell className="text-gray-500 text-sm">{format(new Date(org.created_at), 'MMM d, yyyy')}</TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openEditOrg(org)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDelete('organization', org.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ACTIVITIES TAB */}
          {activeTab === 'activities' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setActivityForm({ type: 'call', subject: '', description: '', deal_id: '', contact_id: '', organization_id: '', due_date: '' }); setShowActivityDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Log Activity
                </Button>
              </div>
              <Card>
                <CardContent className="p-6">
                  {crmActivities.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No activities logged yet</p>
                  ) : (
                    <div className="space-y-4">
                      {crmActivities.map(activity => {
                        const config = ACTIVITY_TYPES[activity.type] || ACTIVITY_TYPES.note;
                        const Icon = config.icon;
                        return (
                          <div key={activity.id} className="flex gap-4 p-4 border rounded-lg hover:bg-gray-50">
                            <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`w-5 h-5 ${config.color}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <h4 className="font-medium">{activity.subject}</h4>
                                <span className="text-xs text-gray-500">{format(new Date(activity.created_at), 'MMM d, h:mm a')}</span>
                              </div>
                              {activity.description && <p className="text-sm text-gray-600">{activity.description}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* PARTNERS TAB */}
          {activeTab === 'partners' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setPartnerForm({ full_name: '', email: '', business_name: '', venue_type: 'Bar & Restaurant', price_level: '$$', phone: '', website: '', address: '', city: 'Denver', state: 'CO', zip_code: '', neighborhood: 'rino', capacity: 50, business_description: '', what_to_expect: '', amenities: [], business_hours: ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => ({day:d,open_time:'11:00',close_time:'23:00',is_closed:false})) }); setAdminPartnerStep(1); setShowPartnerDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create Partner
                </Button>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Partner Applications</CardTitle>
                  <CardDescription>{applications.filter(a => a.status === 'pending').length} pending review</CardDescription>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No applications yet</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Applied</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applications.map(app => (
                          <TableRow key={app.id}>
                            <TableCell>
                              <p className="font-medium">{app.business_name}</p>
                              <p className="text-xs text-gray-500">{app.website}</p>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{app.contact_name}</p>
                              <p className="text-xs text-gray-500">{app.email}</p>
                            </TableCell>
                            <TableCell><Badge variant="outline">{app.business_type}</Badge></TableCell>
                            <TableCell>
                              <Badge className={app.status === 'pending' ? 'bg-amber-100 text-amber-700' : app.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {app.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">{format(new Date(app.created_at), 'MMM d, yyyy')}</TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                {app.status === 'pending' && (
                                  <>
                                    <Button size="sm" onClick={() => handleApplicationAction(app.id, 'approve')} className="bg-green-600 hover:bg-green-700">Approve</Button>
                                    <Button size="sm" variant="outline" onClick={() => handleApplicationAction(app.id, 'reject')} className="text-red-600 border-red-300">Reject</Button>
                                  </>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteApplication(app.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* EVENTS TAB */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEventForm({ title: '', description: '', venue_id: '', date: '', time: '', price: 0, category: 'music', capacity: 100 }); setShowEventDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create Event
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Venue</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEvents.map(event => (
                        <TableRow key={event.id}>
                          <TableCell>
                            <p className="font-medium">{event.title}</p>
                            <p className="text-xs text-gray-500">{event.category}</p>
                          </TableCell>
                          <TableCell>{event.venue_name}</TableCell>
                          <TableCell>{event.date} {event.time}</TableCell>
                          <TableCell>${event.price}</TableCell>
                          <TableCell>
                            <Badge className={event.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                              {event.is_published ? 'Published' : 'Draft'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/partner/events/${event.id}`)}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleEventPublished(event.id, event.is_published)}>
                                  {event.is_published ? <><X className="w-4 h-4 mr-2" />Unpublish</> : <><Check className="w-4 h-4 mr-2" />Publish</>}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDeleteEvent(event.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* EXPERIENCES TAB */}
          {activeTab === 'experiences' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setExperienceForm({ title: '', description: '', venue_id: '', price: 0, duration: '', category: '' }); setShowExperienceDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create Experience
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Experience</TableHead>
                        <TableHead>Venue</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredExperiences.map(exp => (
                        <TableRow key={exp.id}>
                          <TableCell>
                            <p className="font-medium">{exp.title}</p>
                            <p className="text-xs text-gray-500">{exp.category}</p>
                          </TableCell>
                          <TableCell>{exp.venue_name}</TableCell>
                          <TableCell>${exp.price}</TableCell>
                          <TableCell>
                            <Badge className={exp.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                              {exp.active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => window.open(`/experiences/${exp.id}`, '_blank')}><Eye className="w-4 h-4 mr-2" />View Page</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => window.open(`/partner/experiences/${exp.id}/builder`, '_blank')}><Edit className="w-4 h-4 mr-2" />Edit Page</DropdownMenuItem>
                                <DropdownMenuItem onSelect={async () => {
                                  try { await api.put(`/vouchers/${exp.id}`, { active: !(exp.active !== false) }); toast.success(exp.active !== false ? 'Deactivated' : 'Activated'); fetchAllData(); } catch { toast.error('Failed'); }
                                }}>{exp.active !== false ? 'Deactivate' : 'Activate'}</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={async () => {
                                  try { await api.delete(`/vouchers/${exp.id}`); toast.success('Experience deleted'); fetchAllData(); } catch { toast.error('Failed'); }
                                }} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* VENUES TAB */}
          {activeTab === 'venues' && (
            <div className="space-y-6">
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={async () => {
                  toast.info('Seeding Denver venues from Google Places... This takes 2-3 minutes.');
                  try { const res = await api.post('/admin/seed-venues?city=Denver&state=CO&max_venues=50'); toast.success(`${res.data.venues_created} venues created!`); fetchAllData(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }}><Sparkles className="w-4 h-4 mr-2" /> Seed Denver Venues</Button>
                <Button variant="outline" onClick={async () => {
                  try { const res = await api.post('/syndication/seed-denver'); toast.success(res.data.message); } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
                }}><Globe className="w-4 h-4 mr-2" /> Seed Syndication Targets</Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Venue</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Partner</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {venues.map(venue => (
                        <TableRow key={venue.id}>
                          <TableCell><p className="font-medium">{venue.name}</p></TableCell>
                          <TableCell><Badge variant="outline">{venue.category || venue.venue_type}</Badge></TableCell>
                          <TableCell className="text-sm">{venue.address}</TableCell>
                          <TableCell className="text-sm">{venue.partner_name || '-'}</TableCell>
                          <TableCell>
                            <Badge className={venue.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{venue.status || 'active'}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => { const s = (venue.state||'co').toLowerCase(); const c = (venue.city||'denver').toLowerCase(); const n = venue.neighborhood||'rino'; const sl = venue.slug||venue.id; window.open(`/venues/${s}/${c}/${n}/${sl}`, '_blank'); }}><Eye className="w-4 h-4 mr-2" />View Profile</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => { setEditingItem(venue); setShowVenueEditDialog(true); }}><Edit className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => handleDeleteVenue(venue.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* DATE NIGHTS TAB */}
          {activeTab === 'date-nights' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Date Night Packages</CardTitle>
                  <CardDescription>Curated date experiences</CardDescription>
                </CardHeader>
                <CardContent>
                  {dateNightPackages.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No packages yet</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {dateNightPackages.map(pkg => (
                        <Card key={pkg.id} className="overflow-hidden">
                          <CardContent className="p-4">
                            <h3 className="font-semibold">{pkg.title}</h3>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{pkg.description}</p>
                            <div className="flex items-center justify-between mt-3">
                              <span className="font-bold">${pkg.price}</span>
                              <Badge className={pkg.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'}>{pkg.status}</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}


          {activeTab === 'curated-guides' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setGuideForm({ title: '', description: '', icon: 'star', prompts: [] }); setShowGuideDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> New Guide Template
                </Button>
                <Button variant="outline" onClick={async () => {
                  toast.info('Generating guides from inventory...');
                  try { const res = await api.post('/guides/auto/generate?force=true'); toast.success(`${res.data.guides_generated} guides generated!`); fetchAllData(); } catch { toast.error('Failed'); }
                }}>
                  <Sparkles className="w-4 h-4 mr-2" /> Auto-Generate from Inventory
                </Button>
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>Guide Templates</CardTitle>
                  <CardDescription>These templates power the "What Kind of Night Are You Designing?" section. The AI uses them to create personalized guides with venues, events, AND experiences.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Description</TableHead><TableHead>Icon</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {guideTemplates.map(t => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">{t.title}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{t.description}</TableCell>
                          <TableCell><Badge variant="outline">{t.icon}</Badge></TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => { setEditingItem(t); setGuideForm({ title: t.title, description: t.description, icon: t.icon || 'star', prompts: t.prompts || [] }); setShowGuideDialog(true); }}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => { try { await api.delete(`/guides/templates/${t.id}`); toast.success('Deleted'); fetchAllData(); } catch { toast.error('Failed'); } }}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {guideTemplates.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Using default templates. Add custom ones to override.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}


          {/* USERS TAB */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => { setEditingItem(null); setUserForm({ full_name: '', email: '', password: '', role: 'consumer' }); setShowUserDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Create User
                </Button>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Gold Member</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map(u => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium">
                                {u.full_name?.charAt(0) || 'U'}
                              </div>
                              <span className="font-medium">{u.full_name || 'Unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>
                            <Select value={u.role} onValueChange={(v) => handleUpdateUserRole(u.id, v)}>
                              <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="consumer">Consumer</SelectItem>
                                <SelectItem value="partner">Partner</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>{u.is_gold_member ? <Badge className="bg-amber-100 text-amber-700">Gold</Badge> : '-'}</TableCell>
                          <TableCell className="text-sm text-gray-500">{u.created_at ? format(new Date(u.created_at), 'MMM d, yyyy') : '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openEditUser(u)}><Edit className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* FEEDBACK TAB */}
          {activeTab === 'reminders' && (
            <div className="space-y-6" data-testid="reminders-tab">
              {/* Reminder System Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-indigo-600">{reminderStatus?.upcoming_reservations || 0}</p>
                    <p className="text-xs text-gray-500">Upcoming Reservations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {reminderStatus?.reservations?.filter(r => r.reminder_24h_sent).length || 0}
                    </p>
                    <p className="text-xs text-gray-500">24h Reminders Sent</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-amber-600">
                      {reminderStatus?.reservations?.filter(r => r.reminder_2h_sent).length || 0}
                    </p>
                    <p className="text-xs text-gray-500">2h Reminders Sent</p>
                  </CardContent>
                </Card>
              </div>

              {/* Manual Trigger */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Reminder Scheduler</CardTitle>
                      <CardDescription>The scheduler runs automatically every 15 minutes. You can also trigger it manually.</CardDescription>
                    </div>
                    <Button
                      onClick={handleTriggerReminders}
                      disabled={triggeringReminders}
                      data-testid="trigger-reminders-btn"
                    >
                      {triggeringReminders ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4 mr-2" />
                      )}
                      {triggeringReminders ? 'Sending...' : 'Trigger Now'}
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Upcoming Reservations with Reminder Status */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Upcoming Reservations (Next 48h)</CardTitle>
                  <CardDescription>Showing reminder delivery status for each reservation</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Guest</TableHead>
                        <TableHead>Venue</TableHead>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>24h Reminder</TableHead>
                        <TableHead>2h Reminder</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(reminderStatus?.reservations || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                            No upcoming reservations in the next 48 hours
                          </TableCell>
                        </TableRow>
                      ) : (
                        (reminderStatus?.reservations || []).map(r => (
                          <TableRow key={r.id}>
                            <TableCell className="font-medium">{r.user_name || 'Unknown'}</TableCell>
                            <TableCell>{r.venue_name || 'Unknown'}</TableCell>
                            <TableCell>{r.date} at {r.time}</TableCell>
                            <TableCell>
                              {r.reminder_24h_sent ? (
                                <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Sent</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {r.reminder_2h_sent ? (
                                <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" /> Sent</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}


          {activeTab === 'gold-members' && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card className="text-center p-4">
                  <p className="text-3xl font-bold text-amber-600">{users.filter(u => u.is_gold_member).length}</p>
                  <p className="text-xs text-gray-500">Active Gold Members</p>
                </Card>
                <Card className="text-center p-4">
                  <p className="text-3xl font-bold text-green-600">${(users.filter(u => u.is_gold_member).length * 1000).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Annual Revenue</p>
                </Card>
                <Card className="text-center p-4">
                  <p className="text-3xl font-bold text-indigo-600">$1,000</p>
                  <p className="text-xs text-gray-500">Annual Membership</p>
                </Card>
              </div>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Gold Members</CardTitle>
                      <CardDescription>Manage your Gold Club membership base</CardDescription>
                    </div>
                    <Button onClick={async () => {
                      const email = prompt('Enter user email to grant Gold membership:');
                      if (!email) return;
                      const targetUser = users.find(u => u.email === email);
                      if (!targetUser) { toast.error('User not found'); return; }
                      try { await api.put(`/admin/users/${targetUser.id}`, { is_gold_member: true }); toast.success(`${email} is now a Gold Member!`); fetchAllData(); } catch { toast.error('Failed'); }
                    }}>
                      <Plus className="w-4 h-4 mr-2" /> Add Gold Member
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.filter(u => u.is_gold_member).length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No Gold Members yet</TableCell></TableRow>
                      ) : users.filter(u => u.is_gold_member).map(u => (
                        <TableRow key={u.id}>
                          <TableCell><div className="flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold">{u.full_name?.charAt(0) || '?'}</div><span className="font-medium">{u.full_name}</span></div></TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell className="text-sm text-gray-500">{u.gold_since || u.created_at ? format(new Date(u.gold_since || u.created_at), 'MMM d, yyyy') : '-'}</TableCell>
                          <TableCell><Badge className="bg-amber-100 text-amber-700">Active</Badge></TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => {
                              try { await api.put(`/admin/users/${u.id}`, { is_gold_member: false }); toast.success('Gold membership revoked'); fetchAllData(); } catch { toast.error('Failed'); }
                            }}>Revoke</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}


          {activeTab === 'feedback' && (
            <div className="space-y-6">
              <div className="grid grid-cols-5 gap-3">
                <Card className="text-center p-4"><p className="text-2xl font-bold text-blue-600">{feedbackStats?.new || 0}</p><p className="text-xs text-gray-500">New</p></Card>
                <Card className="text-center p-4"><p className="text-2xl font-bold text-amber-600">{feedbackStats?.reviewing || 0}</p><p className="text-xs text-gray-500">Reviewing</p></Card>
                <Card className="text-center p-4"><p className="text-2xl font-bold text-purple-600">{feedbackStats?.in_progress || 0}</p><p className="text-xs text-gray-500">In Progress</p></Card>
                <Card className="text-center p-4"><p className="text-2xl font-bold text-green-600">{feedbackStats?.completed || 0}</p><p className="text-xs text-gray-500">Completed</p></Card>
                <Card className="text-center p-4"><p className="text-2xl font-bold text-red-600">{feedbackStats?.bugs || 0}</p><p className="text-xs text-gray-500">Bugs</p></Card>
              </div>
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredFeedback.map(fb => (
                        <TableRow key={fb.id} className="cursor-pointer" onClick={() => openFeedbackDialog(fb)}>
                          <TableCell><Badge variant="outline">{fb.type?.replace('_', ' ')}</Badge></TableCell>
                          <TableCell>
                            <p className="font-medium line-clamp-1">{fb.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{fb.description?.substring(0, 50)}...</p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm">{fb.user_name || 'Anonymous'}</p>
                            <p className="text-xs text-gray-500">{fb.user_email}</p>
                          </TableCell>
                          <TableCell>
                            <Badge className={
                              fb.status === 'new' ? 'bg-blue-100 text-blue-700' :
                              fb.status === 'reviewing' ? 'bg-amber-100 text-amber-700' :
                              fb.status === 'in_progress' ? 'bg-purple-100 text-purple-700' :
                              fb.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                            }>{fb.status?.replace('_', ' ')}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{format(new Date(fb.created_at), 'MMM d, yyyy')}</TableCell>
                          <TableCell onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => openFeedbackDialog(fb)}><Eye className="w-4 h-4" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* DIALOGS */}
      
      {/* Organization Dialog */}
      <Dialog open={showOrgDialog} onOpenChange={setShowOrgDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Organization' : 'New Organization'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2"><Label>Name *</Label><Input value={orgForm.name} onChange={e => setOrgForm({...orgForm, name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Industry</Label><Select value={orgForm.industry} onValueChange={v => setOrgForm({...orgForm, industry: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{INDUSTRIES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Website</Label><Input value={orgForm.website} onChange={e => setOrgForm({...orgForm, website: e.target.value})} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={orgForm.email} onChange={e => setOrgForm({...orgForm, email: e.target.value})} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={orgForm.phone} onChange={e => setOrgForm({...orgForm, phone: e.target.value})} /></div>
            <div className="col-span-2 space-y-2"><Label>Address</Label><Input value={orgForm.address} onChange={e => setOrgForm({...orgForm, address: e.target.value})} /></div>
            <div className="space-y-2"><Label>City</Label><Input value={orgForm.city} onChange={e => setOrgForm({...orgForm, city: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-2"><div className="space-y-2"><Label>State</Label><Input value={orgForm.state} onChange={e => setOrgForm({...orgForm, state: e.target.value})} /></div><div className="space-y-2"><Label>ZIP</Label><Input value={orgForm.zip_code} onChange={e => setOrgForm({...orgForm, zip_code: e.target.value})} /></div></div>
            <div className="col-span-2 space-y-2"><Label>Notes</Label><Textarea value={orgForm.description} onChange={e => setOrgForm({...orgForm, description: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowOrgDialog(false)}>Cancel</Button><Button onClick={handleSaveOrg} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Contact' : 'New Contact'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2"><Label>First Name *</Label><Input value={contactForm.first_name} onChange={e => setContactForm({...contactForm, first_name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Last Name *</Label><Input value={contactForm.last_name} onChange={e => setContactForm({...contactForm, last_name: e.target.value})} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={contactForm.email} onChange={e => setContactForm({...contactForm, email: e.target.value})} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={contactForm.phone} onChange={e => setContactForm({...contactForm, phone: e.target.value})} /></div>
            <div className="space-y-2"><Label>Job Title</Label><Input value={contactForm.job_title} onChange={e => setContactForm({...contactForm, job_title: e.target.value})} /></div>
            <div className="space-y-2"><Label>Organization</Label><Select value={contactForm.organization_id || '_none'} onValueChange={v => setContactForm({...contactForm, organization_id: v === '_none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="_none">None</SelectItem>{crmOrganizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2 space-y-2"><Label>Lead Source</Label><Select value={contactForm.lead_source || '_none'} onValueChange={v => setContactForm({...contactForm, lead_source: v === '_none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="_none">None</SelectItem>{Object.entries(LEAD_SOURCES).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="col-span-2 space-y-2"><Label>Notes</Label><Textarea value={contactForm.notes} onChange={e => setContactForm({...contactForm, notes: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowContactDialog(false)}>Cancel</Button><Button onClick={handleSaveContact} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deal Dialog */}
      <Dialog open={showDealDialog} onOpenChange={setShowDealDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Deal' : 'New Deal'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2"><Label>Title *</Label><Input value={dealForm.title} onChange={e => setDealForm({...dealForm, title: e.target.value})} placeholder="e.g., Blue Moon Partnership" /></div>
            <div className="space-y-2"><Label>Value ($)</Label><Input type="number" value={dealForm.value} onChange={e => setDealForm({...dealForm, value: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-2"><Label>Stage</Label><Select value={dealForm.stage} onValueChange={v => setDealForm({...dealForm, stage: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PIPELINE_STAGES.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Organization</Label><Select value={dealForm.organization_id || '_none'} onValueChange={v => setDealForm({...dealForm, organization_id: v === '_none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="_none">None</SelectItem>{crmOrganizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Contact</Label><Select value={dealForm.contact_id || '_none'} onValueChange={v => setDealForm({...dealForm, contact_id: v === '_none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="_none">None</SelectItem>{crmContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Expected Close</Label><Input type="date" value={dealForm.expected_close_date} onChange={e => setDealForm({...dealForm, expected_close_date: e.target.value})} /></div>
            <div className="space-y-2"><Label>Probability (%)</Label><Input type="number" min={0} max={100} value={dealForm.probability} onChange={e => setDealForm({...dealForm, probability: parseInt(e.target.value) || 0})} /></div>
            <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={dealForm.description} onChange={e => setDealForm({...dealForm, description: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDealDialog(false)}>Cancel</Button><Button onClick={handleSaveDeal} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Type</Label><Select value={activityForm.type} onValueChange={v => setActivityForm({...activityForm, type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ACTIVITY_TYPES).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Subject *</Label><Input value={activityForm.subject} onChange={e => setActivityForm({...activityForm, subject: e.target.value})} /></div>
            <div className="space-y-2"><Label>Link To</Label>
              <Select value={activityForm.deal_id || activityForm.contact_id || activityForm.organization_id || '_none'} onValueChange={v => {
                if (v === '_none') { setActivityForm({...activityForm, deal_id: '', contact_id: '', organization_id: ''}); return; }
                const deal = crmDeals.find(d => d.id === v);
                const contact = crmContacts.find(c => c.id === v);
                const org = crmOrganizations.find(o => o.id === v);
                setActivityForm({...activityForm, deal_id: deal ? v : '', contact_id: contact ? v : '', organization_id: org ? v : ''});
              }}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Select...</SelectItem>
                  {crmDeals.map(d => <SelectItem key={d.id} value={d.id}>{d.title} (Deal)</SelectItem>)}
                  {crmContacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} (Contact)</SelectItem>)}
                  {crmOrganizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name} (Org)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(activityForm.type === 'task' || activityForm.type === 'follow_up') && (
              <div className="space-y-2"><Label>Due Date</Label><Input type="date" value={activityForm.due_date} onChange={e => setActivityForm({...activityForm, due_date: e.target.value})} /></div>
            )}
            <div className="space-y-2"><Label>Notes</Label><Textarea value={activityForm.description} onChange={e => setActivityForm({...activityForm, description: e.target.value})} rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowActivityDialog(false)}>Cancel</Button><Button onClick={handleSaveActivity} disabled={saving}>{saving ? 'Saving...' : 'Log'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Event</DialogTitle><DialogDescription>Create an event for any venue (Admin only)</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2"><Label>Title *</Label><Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} /></div>
            <div className="col-span-2 space-y-2"><Label>Venue *</Label><Select value={eventForm.venue_id} onValueChange={v => setEventForm({...eventForm, venue_id: v})}><SelectTrigger><SelectValue placeholder="Select venue" /></SelectTrigger><SelectContent>{venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Date</Label><Input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} /></div>
            <div className="space-y-2"><Label>Time</Label><Input type="time" value={eventForm.time} onChange={e => setEventForm({...eventForm, time: e.target.value})} /></div>
            <div className="space-y-2"><Label>Price ($)</Label><Input type="number" value={eventForm.price} onChange={e => setEventForm({...eventForm, price: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-2"><Label>Capacity</Label><Input type="number" value={eventForm.capacity} onChange={e => setEventForm({...eventForm, capacity: parseInt(e.target.value) || 100})} /></div>
            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <Input 
                type="number" 
                value={eventForm.commission_rate} 
                onChange={e => setEventForm({...eventForm, commission_rate: e.target.value})}
                placeholder="Default: 12%"
                min="0"
                max="100"
                step="0.5"
              />
              <p className="text-xs text-muted-foreground">Leave empty for default (12%)</p>
            </div>
            <div className="space-y-2"><Label>Category</Label><Select value={eventForm.category} onValueChange={v => setEventForm({...eventForm, category: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="music">Music</SelectItem><SelectItem value="food">Food & Drink</SelectItem><SelectItem value="art">Art</SelectItem><SelectItem value="comedy">Comedy</SelectItem><SelectItem value="wellness">Wellness</SelectItem><SelectItem value="networking">Networking</SelectItem></SelectContent></Select></div>
            <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEventDialog(false)}>Cancel</Button><Button onClick={handleSaveEvent} disabled={saving}>{saving ? 'Creating...' : 'Create Event'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Experience Dialog */}
      <Dialog open={showExperienceDialog} onOpenChange={setShowExperienceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Create Experience</DialogTitle><DialogDescription>Create an experience for any venue (Admin only)</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2 space-y-2"><Label>Title *</Label><Input value={experienceForm.title} onChange={e => setExperienceForm({...experienceForm, title: e.target.value})} /></div>
            <div className="col-span-2 space-y-2"><Label>Venue *</Label><Select value={experienceForm.venue_id} onValueChange={v => setExperienceForm({...experienceForm, venue_id: v})}><SelectTrigger><SelectValue placeholder="Select venue" /></SelectTrigger><SelectContent>{venues.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Price ($)</Label><Input type="number" value={experienceForm.price} onChange={e => setExperienceForm({...experienceForm, price: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-2"><Label>Valid Until</Label><Input type="date" value={experienceForm.valid_until} onChange={e => setExperienceForm({...experienceForm, valid_until: e.target.value})} /></div>
            <div className="space-y-2">
              <Label>Commission Rate (%)</Label>
              <Input 
                type="number" 
                value={experienceForm.commission_rate} 
                onChange={e => setExperienceForm({...experienceForm, commission_rate: e.target.value})}
                placeholder="Default: 25%"
                min="0"
                max="100"
                step="0.5"
              />
              <p className="text-xs text-muted-foreground">Leave empty for default (25%)</p>
            </div>
            <div className="space-y-2"><Label>Duration</Label><Input value={experienceForm.duration} onChange={e => setExperienceForm({...experienceForm, duration: e.target.value})} placeholder="e.g., 2 hours" /></div>
            <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={experienceForm.description} onChange={e => setExperienceForm({...experienceForm, description: e.target.value})} rows={3} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowExperienceDialog(false)}>Cancel</Button><Button onClick={handleSaveExperience} disabled={saving}>{saving ? 'Creating...' : 'Create Experience'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedFeedback?.title}</DialogTitle>
            <DialogDescription>From {selectedFeedback?.user_name || 'Anonymous'} ({selectedFeedback?.user_email})</DialogDescription>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4 py-4">
              <div className="flex gap-2">
                <Badge variant="outline">{selectedFeedback.type?.replace('_', ' ')}</Badge>
                <Badge>{selectedFeedback.status?.replace('_', ' ')}</Badge>
              </div>
              <div><Label className="text-gray-500">Description</Label><p className="mt-1 p-3 bg-gray-50 rounded-lg">{selectedFeedback.description}</p></div>
              {selectedFeedback.screenshots?.length > 0 && (
                <div><Label className="text-gray-500">Screenshots</Label><div className="flex gap-2 mt-1">{selectedFeedback.screenshots.map((url, i) => <img key={i} src={url} alt="" className="w-24 h-24 object-cover rounded border" />)}</div></div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Status</Label><Select value={feedbackEdits.status} onValueChange={v => setFeedbackEdits({...feedbackEdits, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="new">New</SelectItem><SelectItem value="reviewing">Reviewing</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="declined">Declined</SelectItem></SelectContent></Select></div>
                <div className="space-y-2"><Label>Priority</Label><Select value={feedbackEdits.priority} onValueChange={v => setFeedbackEdits({...feedbackEdits, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="critical">Critical</SelectItem></SelectContent></Select></div>
              </div>
              <div className="space-y-2"><Label>Response (public)</Label><Textarea value={feedbackResponse} onChange={e => setFeedbackResponse(e.target.value)} rows={3} /></div>
              <div className="space-y-2"><Label>Internal Notes</Label><Textarea value={feedbackEdits.admin_notes} onChange={e => setFeedbackEdits({...feedbackEdits, admin_notes: e.target.value})} rows={2} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>Cancel</Button><Button onClick={handleSaveFeedback} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Partner Onboarding Dialog - 5-step flow */}
      <Dialog open={showPartnerDialog} onOpenChange={(open) => { setShowPartnerDialog(open); if (!open) setAdminPartnerStep(1); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Partner</DialogTitle>
            <DialogDescription>Step {adminPartnerStep <= 9 ? `${adminPartnerStep} of 9` : 'Complete'} — Auto-approved with welcome email.</DialogDescription>
          </DialogHeader>
          {adminPartnerStep <= 9 && <div className="flex items-center gap-0.5 mb-2">
            {['Info','Location','Hours','Amenities','Photos','Menu','Reservations','Experience','Review'].map((label, i) => (
              <div key={label} className="flex items-center">
                <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold", i+1 < adminPartnerStep ? "bg-green-500 text-white" : i+1 === adminPartnerStep ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-500")}>{i+1 < adminPartnerStep ? <Check className="w-3 h-3" /> : i+1}</div>
                {i < 8 && <div className={cn("w-3 h-0.5 mx-0.5", i+1 < adminPartnerStep ? "bg-green-500" : "bg-gray-200")} />}
              </div>
            ))}
          </div>}

          {adminPartnerStep === 1 && <div className="space-y-4 py-2">
            {/* Google Place Search */}
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-2 flex items-center gap-1"><Sparkles className="w-4 h-4" /> Quick Start: Import from Google</p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search business on Google..." value={adminPlaceQuery} onChange={e => adminSearchPlace(e.target.value)} className="pl-10" />
                {adminPlaceSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />}
              </div>
              {adminPlaceResults.length > 0 && (
                <div className="mt-1 bg-white dark:bg-gray-900 border rounded-lg shadow-lg max-h-48 overflow-auto">
                  {adminPlaceResults.map(r => (
                    <button key={r.place_id} onClick={() => adminSelectPlace(r.place_id)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 border-b last:border-0 text-sm">
                      <p className="font-medium">{r.structured_formatting?.main_text}</p>
                      <p className="text-xs text-muted-foreground">{r.structured_formatting?.secondary_text}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Contact Name *</Label><Input value={partnerForm.full_name} onChange={e => setPartnerForm({...partnerForm, full_name: e.target.value})} placeholder="Jane Smith" /></div>
              <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={partnerForm.email} onChange={e => setPartnerForm({...partnerForm, email: e.target.value})} placeholder="jane@venue.com" /></div>
              <div className="col-span-2 space-y-1.5"><Label>Business Name *</Label><Input value={partnerForm.business_name} onChange={e => setPartnerForm({...partnerForm, business_name: e.target.value})} placeholder="The Loft RiNo" /></div>
              <div className="space-y-1.5"><Label>Venue Type</Label><Select value={partnerForm.venue_type} onValueChange={v => setPartnerForm({...partnerForm, venue_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{['Bar & Restaurant','Brewery','Wine Bar','Cocktail Lounge','Event Space','Art Gallery','Music Venue','Rooftop Bar','Sports Bar','Coffee Shop','Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Price Level</Label><Select value={partnerForm.price_level || '$$'} onValueChange={v => setPartnerForm({...partnerForm, price_level: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="$">$ Budget</SelectItem><SelectItem value="$$">$$ Moderate</SelectItem><SelectItem value="$$$">$$$ Upscale</SelectItem><SelectItem value="$$$$">$$$$ Fine Dining</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Capacity</Label><Input type="number" value={partnerForm.capacity || 50} onChange={e => setPartnerForm({...partnerForm, capacity: parseInt(e.target.value) || 50})} /></div>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Business Description</Label>
                <Button type="button" variant="outline" size="sm" onClick={adminGenerateDesc} disabled={adminGenDesc || !partnerForm.business_name}>
                  {adminGenDesc ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1 text-indigo-600" /> Generate with AI</>}
                </Button>
              </div>
              <Textarea value={partnerForm.business_description || ''} onChange={e => setPartnerForm({...partnerForm, business_description: e.target.value})} rows={4} placeholder="Describe the venue..." />
              <p className="text-xs text-muted-foreground">{(partnerForm.business_description||'').length}/300</p>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>What to Expect</Label>
                <Button type="button" variant="outline" size="sm" onClick={adminGenerateExpect} disabled={adminGenExpect || !partnerForm.business_name}>
                  {adminGenExpect ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1 text-indigo-600" /> Generate with AI</>}
                </Button>
              </div>
              <Textarea value={partnerForm.what_to_expect || ''} onChange={e => setPartnerForm({...partnerForm, what_to_expect: e.target.value})} rows={2} placeholder="Craft cocktails, live music..." />
            </div>
          </div>}

          {adminPartnerStep === 2 && <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={partnerForm.address} onChange={e => setPartnerForm({...partnerForm, address: e.target.value})} placeholder="2920 Larimer St" /></div>
              <div className="space-y-1.5"><Label>City</Label><Input value={partnerForm.city||'Denver'} onChange={e => setPartnerForm({...partnerForm, city: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-2"><div className="space-y-1.5"><Label>State</Label><Input value={partnerForm.state||'CO'} onChange={e => setPartnerForm({...partnerForm, state: e.target.value})} /></div><div className="space-y-1.5"><Label>ZIP</Label><Input value={partnerForm.zip_code||''} onChange={e => setPartnerForm({...partnerForm, zip_code: e.target.value})} placeholder="80205" /></div></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={partnerForm.phone} onChange={e => setPartnerForm({...partnerForm, phone: e.target.value})} placeholder="(303) 555-0123" /></div>
              <div className="space-y-1.5"><Label>Website</Label><Input value={partnerForm.website||''} onChange={e => setPartnerForm({...partnerForm, website: e.target.value})} placeholder="https://venue.com" /></div>
              <div className="space-y-1.5"><Label>Neighborhood</Label><Select value={partnerForm.neighborhood||'rino'} onValueChange={v => setPartnerForm({...partnerForm, neighborhood: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[{v:'rino',l:'RiNo (River North)'},{v:'cherry-creek',l:'Cherry Creek'},{v:'lodo',l:'LoDo'},{v:'sobo',l:'SoBo'},{v:'highland-lohi',l:'Highland / LoHi'},{v:'capitol-hill',l:'Capitol Hill'},{v:'tennyson',l:'Tennyson'},{v:'other',l:'Other'}].map(n=><SelectItem key={n.v} value={n.v}>{n.l}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>}

          {adminPartnerStep === 3 && <div className="space-y-4 py-2">
            <div className="flex flex-wrap gap-2 mb-2">{[{l:'Restaurant (11am-10pm)',o:'11:00',c:'22:00'},{l:'Bar (4pm-2am)',o:'16:00',c:'02:00'},{l:'All Day (9am-11pm)',o:'09:00',c:'23:00'}].map(p=>(<Button key={p.l} variant="outline" size="sm" onClick={()=>{setPartnerForm({...partnerForm,business_hours:['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d=>({day:d,open_time:p.o,close_time:p.c,is_closed:false}))});toast.success(`Applied ${p.l}`);}}>{p.l}</Button>))}</div>
            <div className="space-y-2">{(partnerForm.business_hours||['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d=>({day:d,open_time:'11:00',close_time:'23:00',is_closed:false}))).map((h,i)=>(<div key={h.day} className="flex items-center gap-3"><span className="w-20 text-sm font-medium capitalize">{h.day}</span><Checkbox checked={!h.is_closed} onCheckedChange={checked=>{const hrs=[...(partnerForm.business_hours||[])];hrs[i]={...hrs[i],is_closed:!checked};setPartnerForm({...partnerForm,business_hours:hrs});}}/><span className="text-xs w-10">{h.is_closed?'Closed':'Open'}</span>{!h.is_closed&&<><Input type="time" value={h.open_time} onChange={e=>{const hrs=[...(partnerForm.business_hours||[])];hrs[i]={...hrs[i],open_time:e.target.value};setPartnerForm({...partnerForm,business_hours:hrs});}} className="w-28"/><span>to</span><Input type="time" value={h.close_time} onChange={e=>{const hrs=[...(partnerForm.business_hours||[])];hrs[i]={...hrs[i],close_time:e.target.value};setPartnerForm({...partnerForm,business_hours:hrs});}} className="w-28"/></>}</div>))}</div>
          </div>}

          {adminPartnerStep === 4 && <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Select all that apply</p>
            <div className="grid grid-cols-3 gap-2">{[...new Set(['Outdoor Seating','Private Rooms','Live Music','Happy Hour','Full Bar','Craft Beer','Wine Selection','Cocktail Menu','Food Menu','Late Night','Pet Friendly','Wheelchair Accessible','Free WiFi','Parking Available','TV/Sports','Dance Floor','DJ','Karaoke','Brunch','Rooftop','Private Dining',...(partnerForm.amenities||[])])].map(a=>(<div key={a} onClick={()=>{const c=partnerForm.amenities||[];setPartnerForm({...partnerForm,amenities:c.includes(a)?c.filter(x=>x!==a):[...c,a]});}} className={cn("p-2 rounded-lg border cursor-pointer text-sm transition-all",(partnerForm.amenities||[]).includes(a)?"border-indigo-500 bg-indigo-50 text-indigo-700":"border-gray-200 hover:border-indigo-300")}>{a}</div>))}</div>
            <div className="flex gap-2 mt-2">
              <Input placeholder="Add custom amenity (Enter)" className="h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const c = partnerForm.amenities||[]; if (!c.includes(e.target.value.trim())) setPartnerForm({...partnerForm, amenities: [...c, e.target.value.trim()]}); e.target.value = ''; }}} />
            </div>
          </div>}

          {adminPartnerStep === 5 && <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Add venue photos. First photo will be the hero image.</p>
            <div className="grid grid-cols-3 gap-3">
              {(partnerForm.photos||[]).map((photo, i) => (
                <div key={i} className="relative group aspect-video rounded-lg overflow-hidden border">
                  <img src={photo} alt="" className="w-full h-full object-cover" />
                  {i === 0 && <Badge className="absolute top-1 left-1 bg-indigo-600 text-white text-[10px]">HERO</Badge>}
                  <button onClick={() => setPartnerForm({...partnerForm, photos: partnerForm.photos.filter((_,idx) => idx !== i)})} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="Paste image URL and press Enter" className="flex-1" onKeyDown={e => { if (e.key === 'Enter' && e.target.value) { setPartnerForm({...partnerForm, photos: [...(partnerForm.photos||[]), e.target.value]}); e.target.value = ''; }}} />
              <Button variant="outline" size="sm" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setPartnerForm(prev => ({...prev, photos: [...(prev.photos||[]), res.data.url]})); toast.success('Uploaded'); } catch { toast.error('Failed'); } }; input.click(); }}><Upload className="w-4 h-4 mr-1" /> Upload</Button>
            </div>
            <p className="text-xs text-muted-foreground">{(partnerForm.photos||[]).length} photos added.</p>
          </div>}

          {adminPartnerStep === 6 && <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Add a link to the venue's menu (PDF or image URL).</p>
            <div className="space-y-1.5">
              <Label>Menu URL (PDF or image link)</Label>
              <Input value={partnerForm.menu_url || ''} onChange={e => setPartnerForm({...partnerForm, menu_url: e.target.value})} placeholder="https://venue.com/menu.pdf" />
            </div>
            <p className="text-xs text-muted-foreground">This will appear as "On The Menu" on the venue profile page.</p>
          </div>}

          {adminPartnerStep === 7 && <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Configure if and how this venue accepts reservations through EVENT★GOERS.</p>
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div><p className="font-medium">Accept Reservations</p><p className="text-xs text-muted-foreground">Allow customers to book tables through your profile</p></div>
              <Checkbox checked={partnerForm.accepts_reservations} onCheckedChange={v => setPartnerForm({...partnerForm, accepts_reservations: v})} />
            </div>
            {partnerForm.accepts_reservations && <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-medium">Require Card Hold</p><p className="text-xs text-muted-foreground">Hold customer's card to prevent no-shows</p></div>
                <Checkbox checked={partnerForm.reservation_settings?.card_required} onCheckedChange={v => setPartnerForm({...partnerForm, reservation_settings: {...partnerForm.reservation_settings, card_required: v}})} />
              </div>
              {partnerForm.reservation_settings?.card_required && <div className="space-y-1.5">
                <Label>No-Show Fee ($ per person)</Label>
                <Input type="number" value={partnerForm.reservation_settings?.no_show_fee || 25} onChange={e => setPartnerForm({...partnerForm, reservation_settings: {...partnerForm.reservation_settings, no_show_fee: parseFloat(e.target.value) || 0}})} />
              </div>}
              <div className="space-y-1.5">
                <Label>Cancellation Window (hours before)</Label>
                <Select value={String(partnerForm.reservation_settings?.cancellation_hours || 24)} onValueChange={v => setPartnerForm({...partnerForm, reservation_settings: {...partnerForm.reservation_settings, cancellation_hours: parseInt(v)}})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="2">2 hours</SelectItem><SelectItem value="6">6 hours</SelectItem><SelectItem value="12">12 hours</SelectItem><SelectItem value="24">24 hours</SelectItem><SelectItem value="48">48 hours</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Advance Booking (days ahead)</Label>
                <Select value={String(partnerForm.reservation_settings?.advance_booking_days || 30)} onValueChange={v => setPartnerForm({...partnerForm, reservation_settings: {...partnerForm.reservation_settings, advance_booking_days: parseInt(v)}})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="7">7 days</SelectItem><SelectItem value="14">14 days</SelectItem><SelectItem value="30">30 days</SelectItem><SelectItem value="60">60 days</SelectItem><SelectItem value="90">90 days</SelectItem></SelectContent>
                </Select>
              </div>
            </div>}
          </div>}

          {adminPartnerStep === 8 && <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Create experiences with The Hook, The Experience, and The Vibe. AI generates all three from just a name.</p>
            {(partnerForm.experiences || []).map((exp, idx) => (
              <div key={idx} className="p-4 border rounded-lg space-y-3 relative">
                <button onClick={() => setPartnerForm({...partnerForm, experiences: partnerForm.experiences.filter((_,i) => i !== idx)})} className="absolute top-2 right-2 text-red-500"><Trash2 className="w-4 h-4" /></button>
                <div className="space-y-1.5"><Label>Experience Name *</Label><Input value={exp.title} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], title: e.target.value}; setPartnerForm({...partnerForm, experiences: exps}); }} placeholder="e.g., Slow Pour & Secrets Cask Tour" /></div>
                <div className="space-y-1.5"><Label>Booking Type</Label>
                  <Select value={exp.booking_type || 'open_redemption'} onValueChange={v => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], booking_type: v}; setPartnerForm({...partnerForm, experiences: exps}); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open_redemption">Voucher — Buy now, redeem later (customer picks date when redeeming)</SelectItem>
                      <SelectItem value="scheduled">Scheduled — Customer picks date/time at purchase</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">{exp.booking_type === 'scheduled' ? 'Customers will select a specific date and time when purchasing' : 'Customers buy a voucher and choose a date when ready to redeem'}</p>
                </div>
                <div className="flex justify-end"><Button variant="outline" size="sm" disabled={adminGenExp || !exp.title} onClick={async () => {
                  const result = await adminGenerateExperience(exp.title);
                  if (result) { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], hook: result.hook || '', experience_description: result.experience_description || '', vibe_tags: result.vibe_tags || [], price: result.price_suggestion || 49, duration: result.duration || '90 minutes', category: result.category || 'entertainment', whats_included: result.what_includes || []}; setPartnerForm({...partnerForm, experiences: exps}); toast.success('Experience generated!'); }
                }}>{adminGenExp ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Generating...</> : <><Sparkles className="w-3.5 h-3.5 mr-1 text-indigo-600" /> Generate with AI</>}</Button></div>
                
                <div className="space-y-1.5"><Label className="text-indigo-600 font-semibold">The Hook</Label><Input value={exp.hook || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], hook: e.target.value}; setPartnerForm({...partnerForm, experiences: exps}); }} placeholder="One compelling sentence — the elevator pitch" /></div>
                
                <div className="space-y-1.5"><Label className="text-indigo-600 font-semibold">The Experience</Label><Textarea value={exp.experience_description || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], experience_description: e.target.value}; setPartnerForm({...partnerForm, experiences: exps}); }} rows={3} placeholder="Full description of what happens during the experience..." /></div>
                
                <div className="space-y-1.5">
                  <Label className="text-indigo-600 font-semibold">The Vibe</Label>
                  <div className="flex flex-wrap gap-2">
                    {[...new Set(['Educational','Exclusive','Behind-the-scenes','Romantic','Social','Adventurous','Relaxing','Creative','Hands-on','VIP','Foodie','Nightlife','Cultural','Outdoors', ...(exp.vibe_tags||[])])].map(tag => (
                      <Badge key={tag} variant={(exp.vibe_tags||[]).includes(tag) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => {
                        const exps = [...partnerForm.experiences]; const current = exps[idx].vibe_tags || [];
                        exps[idx] = {...exps[idx], vibe_tags: current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]};
                        setPartnerForm({...partnerForm, experiences: exps});
                      }}>{tag}</Badge>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-1">
                    <Input placeholder="Add custom vibe tag..." className="h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], vibe_tags: [...(exps[idx].vibe_tags||[]), e.target.value.trim()]}; setPartnerForm({...partnerForm, experiences: exps}); e.target.value = ''; }}} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5"><Label>Price ($)</Label><Input type="number" value={exp.price || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], price: parseFloat(e.target.value) || 0}; setPartnerForm({...partnerForm, experiences: exps}); }} /></div>
                  <div className="space-y-1.5"><Label>Duration</Label><Input value={exp.duration || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], duration: e.target.value}; setPartnerForm({...partnerForm, experiences: exps}); }} placeholder="90 minutes" /></div>
                  <div className="space-y-1.5"><Label>Category</Label><Select value={exp.category || 'entertainment'} onValueChange={v => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], category: v}; setPartnerForm({...partnerForm, experiences: exps}); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="food">Food</SelectItem><SelectItem value="drinks">Drinks</SelectItem><SelectItem value="entertainment">Entertainment</SelectItem><SelectItem value="wellness">Wellness</SelectItem><SelectItem value="art">Art</SelectItem><SelectItem value="music">Music</SelectItem><SelectItem value="adventure">Adventure</SelectItem></SelectContent></Select></div>
                </div>

                <div className="flex flex-wrap gap-3 pt-2 border-t">
                  <div className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer" onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], is_date_night: !exps[idx].is_date_night}; setPartnerForm({...partnerForm, experiences: exps}); }}>
                    <Checkbox checked={exp.is_date_night || false} />
                    <div><p className="text-sm font-medium">Date Night</p><p className="text-[10px] text-muted-foreground">Include in Date Night directory</p></div>
                  </div>
                  <div className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer" onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], is_date_night_exclusive: !exps[idx].is_date_night_exclusive}; setPartnerForm({...partnerForm, experiences: exps}); }}>
                    <Checkbox checked={exp.is_date_night_exclusive || false} />
                    <div><p className="text-sm font-medium">Date Night Exclusive</p><p className="text-[10px] text-muted-foreground">Only available as a date night</p></div>
                  </div>
                  <div className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer" onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], is_eventgoers_exclusive: !exps[idx].is_eventgoers_exclusive}; setPartnerForm({...partnerForm, experiences: exps}); }}>
                    <Checkbox checked={exp.is_eventgoers_exclusive || false} />
                    <div><p className="text-sm font-medium">EVENT★GOERS Exclusive</p><p className="text-[10px] text-muted-foreground">Only available on our platform</p></div>
                  </div>
                </div>

                {/* Availability */}
                <div className="grid grid-cols-3 gap-3 pt-2 border-t">
                  <div className="space-y-1.5"><Label>Valid Until</Label><Input type="date" value={exp.valid_until || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], valid_until: e.target.value}; setPartnerForm({...partnerForm, experiences: exps}); }} /></div>
                  <div className="space-y-1.5"><Label>Qty Available</Label><Input type="number" value={exp.quantity_limit || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], quantity_limit: parseInt(e.target.value) || null}; setPartnerForm({...partnerForm, experiences: exps}); }} placeholder="Unlimited" /></div>
                  <div className="space-y-1.5"><Label>Min Tickets</Label><Input type="number" value={exp.min_tickets || ''} onChange={e => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], min_tickets: parseInt(e.target.value) || 1}; setPartnerForm({...partnerForm, experiences: exps}); }} placeholder="1" /></div>
                </div>

                {/* Photos */}
                <div className="space-y-1.5 pt-2 border-t">
                  <Label>Experience Photos</Label>
                  <div className="flex gap-2 flex-wrap">
                    {(exp.images||[]).map((img, imgIdx) => (
                      <div key={imgIdx} className="relative group w-20 h-14 rounded overflow-hidden">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], images: exps[idx].images.filter((_,i) => i !== imgIdx)}; setPartnerForm({...partnerForm, experiences: exps}); }} className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Paste image URL and press Enter" className="h-8 text-xs flex-1" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], images: [...(exps[idx].images||[]), e.target.value.trim()]}; setPartnerForm({...partnerForm, experiences: exps}); e.target.value = ''; }}} />
                    <Button variant="outline" size="sm" className="h-8 text-xs shrink-0" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], images: [...(exps[idx].images||[]), res.data.url]}; setPartnerForm({...partnerForm, experiences: exps}); toast.success('Photo uploaded'); } catch { toast.error('Upload failed'); } }; input.click(); }}>
                      <Upload className="w-3 h-3 mr-1" /> Upload
                    </Button>
                  </div>
                </div>

                {/* What's Included — editable */}
                <div className="space-y-1.5 pt-2 border-t">
                  <Label>What's Included</Label>
                  {(exp.whats_included||[]).map((item, itemIdx) => (
                    <div key={itemIdx} className="flex gap-2 items-center">
                      <span className="text-green-500 text-sm">✓</span>
                      <Input value={item} className="h-8 text-sm" onChange={e => { const exps = [...partnerForm.experiences]; const items = [...(exps[idx].whats_included||[])]; items[itemIdx] = e.target.value; exps[idx] = {...exps[idx], whats_included: items}; setPartnerForm({...partnerForm, experiences: exps}); }} />
                      <button onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], whats_included: exps[idx].whats_included.filter((_,i) => i !== itemIdx)}; setPartnerForm({...partnerForm, experiences: exps}); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => { const exps = [...partnerForm.experiences]; exps[idx] = {...exps[idx], whats_included: [...(exps[idx].whats_included||[]), '']}; setPartnerForm({...partnerForm, experiences: exps}); }}><Plus className="w-3 h-3 mr-1" /> Add item</Button>
                </div>

                {/* Terms & Conditions */}
                <div className="space-y-1.5 pt-2 border-t">
                  <Label>Terms & Conditions</Label>
                  {(exp.terms||['Valid for single use only','Non-refundable after purchase','Must be redeemed before expiry date','Subject to availability']).map((term, termIdx) => (
                    <div key={termIdx} className="flex gap-2 items-center">
                      <span className="text-muted-foreground text-sm">•</span>
                      <Input value={term} className="h-8 text-sm" onChange={e => { const exps = [...partnerForm.experiences]; const terms = [...(exps[idx].terms||['Valid for single use only','Non-refundable after purchase','Must be redeemed before expiry date','Subject to availability'])]; terms[termIdx] = e.target.value; exps[idx] = {...exps[idx], terms}; setPartnerForm({...partnerForm, experiences: exps}); }} />
                      <button onClick={() => { const exps = [...partnerForm.experiences]; const terms = [...(exps[idx].terms||[])]; terms.splice(termIdx, 1); exps[idx] = {...exps[idx], terms}; setPartnerForm({...partnerForm, experiences: exps}); }} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" onClick={() => { const exps = [...partnerForm.experiences]; const terms = [...(exps[idx].terms||[])]; terms.push(''); exps[idx] = {...exps[idx], terms}; setPartnerForm({...partnerForm, experiences: exps}); }}><Plus className="w-3 h-3 mr-1" /> Add term</Button>
                </div>
                {(exp.whats_included||[]).length > 0 && <div><Label className="text-xs">What's Included</Label><ul className="text-xs text-muted-foreground mt-1">{exp.whats_included.map((item, i) => <li key={i}>• {item}</li>)}</ul></div>}
              </div>
            ))}
            <Button variant="outline" className="w-full" onClick={() => setPartnerForm({...partnerForm, experiences: [...(partnerForm.experiences || []), { title: '', hook: '', experience_description: '', vibe_tags: [], price: 0, duration: '', category: 'entertainment', whats_included: [] }]})}><Plus className="w-4 h-4 mr-2" /> Add Experience</Button>
            <p className="text-xs text-muted-foreground text-center">Optional — experiences can also be added later from the partner dashboard.</p>
          </div>}

          {adminPartnerStep === 9 && <div className="space-y-4 py-2">
            <div className="p-4 bg-gray-50 rounded-lg"><h3 className="font-bold text-lg">{partnerForm.business_name}</h3><div className="flex flex-wrap gap-2 mt-1 mb-2"><Badge>{partnerForm.venue_type}</Badge><Badge variant="outline">{partnerForm.price_level}</Badge><Badge variant="outline">{partnerForm.capacity} cap</Badge>{partnerForm.accepts_reservations && <Badge className="bg-green-100 text-green-700">Reservations ON</Badge>}</div>{partnerForm.business_description&&<p className="text-sm text-muted-foreground">{partnerForm.business_description}</p>}</div>
            <div className="grid grid-cols-2 gap-4 text-sm"><div><p className="text-muted-foreground">Contact</p><p className="font-medium">{partnerForm.full_name}</p><p>{partnerForm.email}</p></div><div><p className="text-muted-foreground">Location</p><p className="font-medium">{partnerForm.address}</p><p>{partnerForm.phone}</p></div></div>
            {(partnerForm.photos||[]).length > 0 && <div><p className="text-muted-foreground text-sm mb-1">Photos ({partnerForm.photos.length})</p><div className="flex gap-2 overflow-x-auto">{partnerForm.photos.slice(0,4).map((p,i) => <img key={i} src={p} alt="" className="w-16 h-12 object-cover rounded" />)}</div></div>}
            {(partnerForm.amenities||[]).length>0&&<div><p className="text-muted-foreground text-sm mb-1">Amenities ({partnerForm.amenities.length})</p><div className="flex flex-wrap gap-1">{partnerForm.amenities.map(a=><Badge key={a} variant="secondary" className="text-xs">{a}</Badge>)}</div></div>}
            {(partnerForm.experiences||[]).length > 0 && <div><p className="text-muted-foreground text-sm mb-1">Experiences ({partnerForm.experiences.length})</p>{partnerForm.experiences.map((e,i) => <p key={i} className="text-sm">{e.title} — ${e.price}</p>)}</div>}
            <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">This partner will be <strong>auto-approved</strong> and receive a welcome email with login credentials.</div>
          </div>}

          {adminPartnerStep === 10 && <div className="space-y-4 py-4 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto"><Check className="w-8 h-8 text-green-600" /></div>
            <h3 className="text-xl font-bold">Partner Created Successfully!</h3>
            <p className="text-muted-foreground">Share these login credentials with the partner:</p>
            <div className="p-4 bg-gray-50 rounded-lg border text-left space-y-2">
              <div><p className="text-xs text-muted-foreground">Email</p><p className="font-mono font-bold">{createdPartnerCreds?.email}</p></div>
              <div><p className="text-xs text-muted-foreground">Temporary Password</p><p className="font-mono font-bold text-indigo-600">{createdPartnerCreds?.password}</p></div>
            </div>
            <p className="text-xs text-muted-foreground">A welcome email with these credentials has also been sent.</p>
          </div>}

          <DialogFooter className="flex justify-between">
            <div>{adminPartnerStep > 1 && adminPartnerStep <= 9 && <Button variant="outline" onClick={() => setAdminPartnerStep(s => s - 1)}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setShowPartnerDialog(false); setAdminPartnerStep(1); setCreatedPartnerCreds(null); }}>
                {adminPartnerStep === 10 ? 'Close' : 'Cancel'}
              </Button>
              {adminPartnerStep < 9 && <Button onClick={() => { if (adminPartnerStep===1&&(!partnerForm.full_name||!partnerForm.email||!partnerForm.business_name)){toast.error('Name, email, and business name required');return;} setAdminPartnerStep(s=>s+1); }}>Next <ChevronRight className="w-4 h-4 ml-1" /></Button>}
              {adminPartnerStep === 9 && <Button onClick={handleCreatePartner} disabled={saving} className="bg-green-600 hover:bg-green-700">{saving?'Creating...':'Create & Auto-Approve'}</Button>}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Venue Edit Dialog */}
      <Dialog open={showVenueEditDialog} onOpenChange={setShowVenueEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Venue</DialogTitle></DialogHeader>
          {editingItem && (
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="col-span-2 space-y-2"><Label>Name</Label><Input value={editingItem.name || ''} onChange={e => setEditingItem({...editingItem, name: e.target.value})} /></div>
              <div className="col-span-2 space-y-2"><Label>Description</Label><Textarea value={editingItem.description || ''} onChange={e => setEditingItem({...editingItem, description: e.target.value})} rows={3} /></div>
              <div className="col-span-2 space-y-2"><Label>Address</Label><Input value={editingItem.address || ''} onChange={e => setEditingItem({...editingItem, address: e.target.value})} /></div>
              <div className="space-y-2"><Label>Phone</Label><Input value={editingItem.phone || ''} onChange={e => setEditingItem({...editingItem, phone: e.target.value})} /></div>
              <div className="space-y-2"><Label>Website</Label><Input value={editingItem.website || ''} onChange={e => setEditingItem({...editingItem, website: e.target.value})} /></div>
              <div className="space-y-2"><Label>Category</Label><Input value={editingItem.category || ''} onChange={e => setEditingItem({...editingItem, category: e.target.value})} /></div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={editingItem.status || 'approved'} onValueChange={v => setEditingItem({...editingItem, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="approved">Approved</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => { setShowVenueEditDialog(false); setEditingItem(null); }}>Cancel</Button><Button onClick={handleSaveVenueEdit} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Edit User' : 'Create New User'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Full Name *</Label><Input value={userForm.full_name} onChange={e => setUserForm({...userForm, full_name: e.target.value})} placeholder="John Doe" /></div>
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} placeholder="john@example.com" /></div>
            {!editingItem && <div className="space-y-2"><Label>Password *</Label><Input type="password" value={userForm.password} onChange={e => setUserForm({...userForm, password: e.target.value})} placeholder="Min 6 characters" /></div>}
            <div className="space-y-2"><Label>Role</Label><Select value={userForm.role} onValueChange={v => setUserForm({...userForm, role: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="consumer">Consumer</SelectItem><SelectItem value="partner">Partner</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowUserDialog(false); setEditingItem(null); }}>Cancel</Button><Button onClick={handleSaveUser} disabled={saving}>{saving ? 'Saving...' : editingItem ? 'Update User' : 'Create User'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide Template Dialog */}
      <Dialog open={showGuideDialog} onOpenChange={setShowGuideDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingItem ? 'Edit Guide Template' : 'New Guide Template'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Title *</Label><Input value={guideForm.title} onChange={e => setGuideForm({...guideForm, title: e.target.value})} placeholder="Perfect Date Night" /></div>
            <div><Label>Description</Label><Input value={guideForm.description} onChange={e => setGuideForm({...guideForm, description: e.target.value})} placeholder="A romantic evening exploring Denver" /></div>
            <div><Label>Icon</Label>
              <Select value={guideForm.icon} onValueChange={v => setGuideForm({...guideForm, icon: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="heart">Heart</SelectItem><SelectItem value="beer">Beer</SelectItem><SelectItem value="palette">Palette</SelectItem><SelectItem value="utensils">Utensils</SelectItem><SelectItem value="sun">Sun</SelectItem><SelectItem value="moon">Moon</SelectItem><SelectItem value="star">Star</SelectItem><SelectItem value="music">Music</SelectItem><SelectItem value="gift">Gift</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>AI Prompts (keywords the AI uses to match content)</Label>
              {(guideForm.prompts||[]).map((p, i) => (
                <div key={i} className="flex gap-2 mt-1"><Input value={p} onChange={e => { const ps = [...guideForm.prompts]; ps[i] = e.target.value; setGuideForm({...guideForm, prompts: ps}); }} /><Button variant="ghost" size="sm" onClick={() => setGuideForm({...guideForm, prompts: guideForm.prompts.filter((_,idx) => idx !== i)})}><X className="w-4 h-4 text-red-500" /></Button></div>
              ))}
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => setGuideForm({...guideForm, prompts: [...(guideForm.prompts||[]), '']})}><Plus className="w-3 h-3 mr-1" /> Add prompt</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGuideDialog(false)}>Cancel</Button>
            <Button disabled={saving} onClick={async () => {
              if (!guideForm.title) { toast.error('Title required'); return; }
              setSaving(true);
              try {
                if (editingItem) { await api.put(`/guides/templates/${editingItem.id}`, guideForm); }
                else { await api.post('/guides/templates', guideForm); }
                toast.success('Saved');
                setShowGuideDialog(false);
                fetchAllData();
              } catch { toast.error('Failed'); }
              finally { setSaving(false); }
            }}>{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
